import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@components/Card';
import { Button } from '@components/Button';
import { Loading } from '@components/Loading';
import { projectService, type AnalysisResponse, type Project } from '@services/projectService';

/**
 * Code Analysis page
 * Shows analysis results and project details
 */
export function CodeAnalysisPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      loadAnalysis();
    }
  }, [projectId]);

  const loadAnalysis = async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Load project details
      const projectData = await projectService.getProject(projectId);
      setProject(projectData);

      // Load analysis results if available
      if (projectData.analysis_status === 'analyzed' || projectData.analysis_status === 'completed') {
        const analysisData = await projectService.getAnalysisResults(projectId);
        setAnalysis(analysisData);
      }
    } catch (err: any) {
      console.error('Failed to load analysis:', err);
      setError(err.response?.data?.detail || 'Failed to load analysis results');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading size="lg" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-red-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading analysis</h3>
          <p className="mt-1 text-sm text-gray-600">{error}</p>
          <div className="mt-6">
            <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  const totalTechStack = [
    ...(analysis?.frontend_stack || []),
    ...(analysis?.backend_stack || []),
    ...(analysis?.database_stack || []),
    ...(analysis?.infrastructure_stack || []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          </div>
          <p className="mt-1 text-gray-600">{project.description || 'Code analysis results'}</p>
        </div>
      </div>

      {/* Project Info */}
      <Card variant="bordered">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600">Source Type</p>
              <p className="mt-1 text-lg font-semibold text-gray-900 capitalize">{project.source_type}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Location</p>
              <p className="mt-1 text-lg font-semibold text-gray-900 truncate" title={project.source_location}>
                {project.source_location}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Branch</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{project.source_branch}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Status</p>
              <span className="mt-1 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-600 border border-green-200">
                {project.analysis_status}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {analysis ? (
        <>
          {/* Statistics */}
          <div className="grid gap-6 md:grid-cols-4">
            <Card variant="bordered">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Total Files</p>
                  <p className="text-3xl font-bold text-primary-400 mt-2">{analysis.total_files.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Lines of Code</p>
                  <p className="text-3xl font-bold text-secondary-400 mt-2">{analysis.total_lines.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Languages</p>
                  <p className="text-3xl font-bold text-accent-400 mt-2">{Object.keys(analysis.languages).length}</p>
                </div>
              </CardContent>
            </Card>

            <Card variant="bordered">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Frameworks</p>
                  <p className="text-3xl font-bold text-purple-400 mt-2">{totalTechStack.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Language Distribution */}
          <Card variant="elevated">
            <CardHeader>
              <CardTitle>Language Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(analysis.languages)
                  .sort(([, a], [, b]) => b - a)
                  .map(([lang, count]) => {
                    const total = Object.values(analysis.languages).reduce((a, b) => a + b, 0);
                    const percentage = ((count / total) * 100).toFixed(1);
                    return (
                      <div key={lang}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{lang}</span>
                          <span className="text-sm text-gray-600">{percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-500 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* Tech Stack */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Frontend Stack */}
            {analysis.frontend_stack.length > 0 && (
              <Card variant="elevated">
                <CardHeader>
                  <CardTitle>Frontend Stack</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.frontend_stack.map((tech) => (
                      <div key={tech.name} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{tech.name}</p>
                          {tech.version && <p className="text-sm text-gray-600">v{tech.version}</p>}
                        </div>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {(tech.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Backend Stack */}
            {analysis.backend_stack.length > 0 && (
              <Card variant="elevated">
                <CardHeader>
                  <CardTitle>Backend Stack</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.backend_stack.map((tech) => (
                      <div key={tech.name} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{tech.name}</p>
                          {tech.version && <p className="text-sm text-gray-600">v{tech.version}</p>}
                        </div>
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded">
                          {(tech.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Database Stack */}
            {analysis.database_stack.length > 0 && (
              <Card variant="elevated">
                <CardHeader>
                  <CardTitle>Database Stack</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.database_stack.map((tech) => (
                      <div key={tech.name} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{tech.name}</p>
                          {tech.version && <p className="text-sm text-gray-600">v{tech.version}</p>}
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded">
                          {(tech.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Infrastructure Stack */}
            {analysis.infrastructure_stack.length > 0 && (
              <Card variant="elevated">
                <CardHeader>
                  <CardTitle>Infrastructure</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.infrastructure_stack.map((tech) => (
                      <div key={tech.name} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{tech.name}</p>
                          {tech.version && <p className="text-sm text-gray-600">v{tech.version}</p>}
                        </div>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {(tech.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Recommendations */}
          {analysis.recommendations.length > 0 && (
            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Modernization Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analysis.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-gray-700">{rec}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card variant="elevated">
          <CardContent className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No analysis results yet</h3>
            <p className="mt-1 text-sm text-gray-600">
              Analysis has not been run for this project. Start the analysis to see results.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
